<script setup lang="ts">
import { computed, onMounted, reactive, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { DateTime } from "luxon";

import type { Calendar, CalendarEvent } from "@/Modules/Calendars/Models/Domain";
import type { ValueType } from "@/Common/Models/Ui/Input";
import { CALENDAR_TYPE } from "@/Modules/Calendars/Models/Common";

import TcInput from "@/Components/UI/TcInput.vue";
import TcSelect, { type Option } from "@/Components/UI/TcSelect.vue";
import TcTextarea from "@/Components/UI/TcTextarea.vue";

import useCalendarsStore from "@/Modules/Calendars/Store";

import { isValidString } from "@/Utils/String";
import { hasValue } from "@/Utils/Objects";
import { unixSecondsToDateTime } from "@/Utils/Date";

export interface CalendarEventFormProps {
  modelValue: Partial<CalendarEvent>;
  disabled?: boolean;
  timezone: string;
}

const { t } = useI18n();
const calendarsStore = useCalendarsStore();

const auxDate = reactive<{ date?: string; startTime?: string; endTime?: string }>({});
const selectedDate = reactive<{ date?: DateTime; startTime?: number; endTime?: number }>({
  date: undefined,
  startTime: undefined,
  endTime: undefined,
});

const props = defineProps<CalendarEventFormProps>();

const emit = defineEmits<{
  (event: "update:modelValue", value: Partial<CalendarEvent>): void;
}>();

const nativeCalendars = computed((): Calendar[] => {
  return calendarsStore.calendars.filter((c) => c.type === CALENDAR_TYPE.NATIVE);
});

const hasMultipleNativeCalendars = computed((): boolean => {
  return calendarsStore.calendars.filter((c) => c.type === CALENDAR_TYPE.NATIVE).length > 1;
});

const selectedCalendar = computed((): Calendar | undefined => {
  return calendarsStore.calendars.find((c) => c.uuid === props.modelValue.calendarUuid);
});

const selectedPatient = computed((): Option | undefined => {
  const patient = calendarsStore.patients.find((p) => p.id === props.modelValue.patientUuid);

  if (!hasValue(patient)) return undefined;

  return { value: patient.id, label: patient.username };
});

const selectedCalendarOption = computed((): Option | undefined => {
  if (!hasValue(selectedCalendar.value)) return undefined;

  return {
    value: selectedCalendar.value.uuid,
    label: selectedCalendar.value.name,
  };
});

const emitUpdateModelValue = (value: Partial<CalendarEvent>) => {
  emit("update:modelValue", value);
};

const handleChangeProp = (prop: keyof CalendarEvent) => (value: unknown) => {
  emitUpdateModelValue({ ...props.modelValue, [prop]: value });
};

const handleUpdateDate = (value: ValueType) => {
  if (isValidString(value)) {
    selectedDate.date = DateTime.fromFormat(value as string, "yyyy-MM-dd", { zone: props.timezone });
  } else {
    selectedDate.date = undefined;
  }

  handleUpdateTimes();
};

const timeToSeconds = (time: string): number => {
  // time HH:mm
  const [hour, minute] = time.split(":");

  return parseInt(hour!) * 60 * 60 + parseInt(minute!) * 60;
};

const handleUpdateStartAt = (value: ValueType) => {
  if (isValidString(value)) {
    selectedDate.startTime = timeToSeconds(value);
  } else {
    selectedDate.startTime = undefined;
  }

  handleUpdateTimes();
};

const handleUpdateEndAt = (value: ValueType) => {
  if (isValidString(value)) {
    selectedDate.endTime = timeToSeconds(value);
  } else {
    selectedDate.endTime = undefined;
  }

  handleUpdateTimes();
};

const handleUpdateTimes = () => {
  if (!hasValue(selectedDate.date)) {
    emitUpdateModelValue({ ...props.modelValue, startAt: undefined, endAt: undefined });
  } else if (!hasValue(selectedDate.startTime)) {
    emitUpdateModelValue({ ...props.modelValue, startAt: undefined });
  } else if (!hasValue(selectedDate.endTime)) {
    emitUpdateModelValue({ ...props.modelValue, endAt: undefined });
  } else {
    const baseTime = selectedDate.date.set({ hour: 0, minute: 0, millisecond: 0 }).toUnixInteger();

    emitUpdateModelValue({
      ...props.modelValue,
      startAt: baseTime + selectedDate.startTime,
      endAt: baseTime + selectedDate.endTime,
    });
  }
};

const handleSelectPatient = (value?: Option) => {
  if (isValidString(value?.value)) {
    emitUpdateModelValue({ ...props.modelValue, patientUuid: value.value });
  }
};

const handleSelectCalendar = (value?: Option) => {
  if (isValidString(value?.value)) {
    emitUpdateModelValue({ ...props.modelValue, calendarUuid: value.value });
  }
};

const prepareDates = (startAt: number, endAt?: number, timeZone?: string) => {
  const currentDate = DateTime.now();
  let currentZone: string = !isValidString(timeZone) ? currentDate.zoneName : timeZone;
  const startDate = unixSecondsToDateTime(startAt, { zone: currentZone });
  let currentEndAt: number = !hasValue(endAt) ? startDate.toUnixInteger() + 50 * 60 : endAt;
  const endDate = unixSecondsToDateTime(currentEndAt, { zone: currentZone });

  auxDate.date = startDate.toFormat("yyyy-MM-dd");
  auxDate.startTime = startDate.toFormat("HH:mm");
  auxDate.endTime = endDate.toFormat("HH:mm");

  selectedDate.date = startDate.set({ hour: 0, minute: 0, millisecond: 0 });
  selectedDate.startTime = timeToSeconds(auxDate.startTime);
  selectedDate.endTime = timeToSeconds(auxDate.endTime);

  handleUpdateTimes();
};

onMounted(() => {
  if (!isValidString(props.modelValue.uuid)) {
    let toUpdate = {};

    if (!hasValue(props.modelValue) || !isValidString(props.modelValue.calendarUuid)) {
      toUpdate = { calendarUuid: nativeCalendars.value[0]!.uuid };
    }

    if (hasValue(props.modelValue.startAt)) {
      prepareDates(props.modelValue.startAt, props.modelValue.endAt, props.modelValue.timeZone);
    } else {
      const now = DateTime.utc().setZone(props.timezone);

      auxDate.date = now.toFormat("yyyy-MM-dd");
      selectedDate.date = now;

      toUpdate = { ...toUpdate, timeZone: props.timezone };
    }

    nextTick(() => {
      emitUpdateModelValue({ ...props.modelValue, ...toUpdate });
    });
  } else {
    const event: CalendarEvent = props.modelValue as CalendarEvent;

    prepareDates(event.startAt, event.endAt, props.timezone);
  }
});
</script>

<template>
  <div class="event_form">
    <TcSelect
      v-if="hasMultipleNativeCalendars"
      class="event_form__calendars__select"
      name="calendar_event_form"
      :label="t('calendars.view.calendar.event_form.calendar')"
      :modelValue="selectedCalendarOption"
      :options="calendarsStore.calendars.map((c) => ({ value: c.uuid, label: c.name }))"
      :disabled="props.disabled"
      @change="handleSelectCalendar"
    />

    <TcInput
      :modelValue="props.modelValue.summary"
      :label="t('calendars.view.calendar.event_form.summary')"
      :disabled="props.disabled"
      @change="(e) => handleChangeProp('summary')(e)"
    />

    <TcSelect
      v-if="calendarsStore.patients.length > 0"
      name="patient_event_form"
      :modelValue="selectedPatient"
      :label="t('calendars.view.calendar.event_form.patient')"
      :placeholder="t('calendars.view.calendar.event_form.patient.placeholder')"
      :options="calendarsStore.patients.map((p) => ({ value: p.id, label: p.username }))"
      :disabled="props.disabled"
      @change="handleSelectPatient"
    />

    <div class="event_form__time">
      <TcInput
        v-model="auxDate.date"
        class="event_form__time__date"
        type="date"
        :disabled="props.disabled"
        :label="t('calendars.view.calendar.event_form.date')"
        @change="handleUpdateDate"
      />

      <TcInput
        v-model="auxDate.startTime"
        class="event_form__time__start_time"
        type="time"
        :label="t('calendars.view.calendar.event_form.start_time')"
        :disabled="props.disabled"
        @change="handleUpdateStartAt"
      />

      <TcInput
        v-model="auxDate.endTime"
        class="event_form__time__end_time"
        type="time"
        :label="t('calendars.view.calendar.event_form.end_time')"
        :disabled="props.disabled"
        @change="handleUpdateEndAt"
      />
    </div>

    <TcTextarea
      :modelValue="!isValidString(props.modelValue.description) ? '' : props.modelValue.description"
      :label="t('calendars.view.calendar.event_form.description')"
      @change="(e) => handleChangeProp('description')(e)"
    />
  </div>
</template>

<style scoped lang="scss">
.event_form {
  &__time {
    display: grid;
    grid-template-areas: "date start_time end_time";
    gap: $space-4;

    @media (max-width: $breakpoint-l) {
      grid-template-columns: 1fr 1fr;
      grid-template-areas:
        "date date"
        "start_time end_time";
    }

    &__date {
      grid-area: date;
    }

    &__start_time {
      grid-area: start_time;
    }

    &__end_time {
      grid-area: end_time;
    }
  }
}
</style>
